import type { ISbStoryData } from "@storyblok/react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStoryblokProxy } from "./useStoryblokProxy";
import { usePreservedSearchQuery } from "./usePreservedSearchQuery";
import type { StoryblokLink } from "../types/ButtonBlockTypes";
import { getCurrentLang } from "@utils/getCurrentLang";
import { getLanguageFromPathname } from "@utils/lang";
import {
  getParentStoryblokPath,
  normalizeStoryblokPath,
  stripRouteLanguage,
} from "@utils/departmentPaths";
import {
  getEditorTokenParams,
  isStoryblokEditor,
} from "@utils/storyblokEditor";

const localizePath = (path: string, language: string) =>
  `/${[language === "en" ? "en" : "", normalizeStoryblokPath(path)]
    .filter(Boolean)
    .join("/")}`;

const useCmsParent = (
  story: ISbStoryData | undefined,
  route: string,
  language: string,
  enabled: boolean
) => {
  const canonicalPath = normalizeStoryblokPath(
    story?.default_full_slug ??
      stripRouteLanguage(story?.full_slug ?? "", language)
  );
  const folderPath = getParentStoryblokPath(canonicalPath);
  const lookupEnabled = Boolean(enabled && story);
  const parentLink = story?.content?.parentPage as StoryblokLink | undefined;
  const parentId = parentLink?.linktype === "story" ? parentLink.id : "";
  const explicitLookupEnabled =
    lookupEnabled && Boolean(parentId) && parentId !== story?.uuid;
  const explicitQuery = useStoryblokProxy(
    {
      by_uuids: parentId || "",
      language: language === "en" ? "en" : undefined,
      per_page: 1,
    },
    !explicitLookupEnabled
  );

  const getTarget = (target?: ISbStoryData) => {
    if (
      !target ||
      target.uuid === story?.uuid ||
      (!Array.isArray(target.content?.body) &&
        target.content?.component !== "Department")
    )
      return undefined;
    const translation = target.translated_slugs?.find(
      (entry) => entry.lang === language && entry.published !== false
    );
    const path =
      language === "en"
        ? translation?.path || target.default_full_slug || target.full_slug
        : target.default_full_slug || target.full_slug;
    if (!normalizeStoryblokPath(path)) return undefined;
    const targetPath = localizePath(
      stripRouteLanguage(path, language),
      language
    );
    if (targetPath === localizePath(route, language)) return undefined;
    return { pathname: targetPath, name: translation?.name || target.name };
  };
  const explicitParent =
    explicitLookupEnabled && !explicitQuery.isError && !explicitQuery.isLoading
      ? getTarget(
          explicitQuery.data?.stories?.find((entry) => entry.uuid === parentId)
        )
      : undefined;
  const folderLookupEnabled =
    lookupEnabled &&
    Boolean(folderPath) &&
    (!explicitLookupEnabled || (!explicitQuery.isLoading && !explicitParent));
  const folderQuery = useStoryblokProxy(
    {
      slug: folderPath || undefined,
      language: language === "en" ? "en" : undefined,
    },
    !folderLookupEnabled
  );
  const folderStory = folderQuery.data?.story;
  const folderParent =
    folderLookupEnabled &&
    !folderQuery.isError &&
    !folderQuery.isLoading &&
    folderStory?.is_startpage &&
    normalizeStoryblokPath(
      folderStory.default_full_slug ??
        stripRouteLanguage(folderStory.full_slug, language)
    ) === folderPath
      ? getTarget(folderStory)
      : undefined;
  return explicitParent ?? folderParent;
};

export const useParentNavigation = (story?: ISbStoryData) => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { preservedSearch, linkState } = usePreservedSearchQuery();
  const editor = isStoryblokEditor(searchParams);
  const previewLanguage = editor ? searchParams.get("_storyblok_lang") : null;
  const supportedPreviewLanguage = ["en", "nl", "default"].includes(
    previewLanguage ?? ""
  )
    ? previewLanguage
    : null;
  const urlLanguage = getLanguageFromPathname(pathname);
  const language = getCurrentLang(supportedPreviewLanguage, urlLanguage);
  const route = stripRouteLanguage(pathname, urlLanguage);
  const homePath = localizePath("", language);
  const isNews = route.startsWith("news/");
  const isVacancy = route.startsWith("vacancies/");
  const preserveListing = isNews || isVacancy;
  const target = useCmsParent(
    story,
    route,
    language,
    Boolean(route) && !preserveListing
  );
  let overviewPath = homePath;
  let overviewLabel = "Toolbar.home";
  if (isNews) {
    overviewLabel = "Toolbar.newsOverview";
  } else if (isVacancy) {
    overviewPath = localizePath("vacancies", language);
    overviewLabel = "Toolbar.vacanciesOverview";
  }
  const parentPath = target?.pathname ?? overviewPath;
  const parentName = target?.name ?? t(overviewLabel, { lng: language });

  const withPreview = (params: URLSearchParams) => {
    if (editor) {
      for (const [key, value] of Object.entries(
        getEditorTokenParams(searchParams)
      )) {
        params.set(key, value);
      }
      if (supportedPreviewLanguage) {
        params.set("_storyblok_lang", supportedPreviewLanguage);
      }
    }
    const query = params.toString();
    return query ? `?${query}` : "";
  };
  const homeSearch = withPreview(new URLSearchParams());
  const parentSearch = withPreview(
    new URLSearchParams(preserveListing ? preservedSearch : "")
  );

  return {
    homeHref: `${homePath}${homeSearch}`,
    parent: route
      ? {
          to: { pathname: parentPath, search: parentSearch },
          label: parentName,
          state: preserveListing ? linkState : undefined,
        }
      : null,
  };
};
